// Doc Order — typed thin client over the `doc-order` edge function.
// One invoke wrapper normalizes supabase-js's FunctionsHttpError (non-2xx bodies
// arrive on error.context) into a status + parsed body so the hook can branch on
// 409 revision conflicts, 422 sign blockers, and 502 retryable pull failures.
import { supabase } from '@/utils/supabase';
import type {
  ChecklistDoc,
  DocOrderFee,
  DocOrderStage,
  DocOrderViewer,
  InsuranceInfo,
  VestingWorkflowState
} from '@/components/encompass/prospects/doc-order/core';

export interface DocOrderTeamMember {
  role: string | null;
  roleName: string | null;
  id: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
}

export interface DocOrderSnapshot {
  pulled: {
    vesting: string | null;
    totalLoanAmount: number | null;
    baseLoanAmount: number | null;
    lenderCredit: number | null;
    cashToFromBorrower: number | null;
    occupancy: string | null;
    propertyState: string | null;
    channel: string | null;
    initialCdSentDate: string | null;
    gfeDate: string | null;
    /** R4 #15 — Field 1401 (loan program), the DSCR signal; distinct from the
     *  displayed lock program. Optional: legacy snapshots predate the pull. */
    loanProgram1401?: string | null;
    /** R5 #4 — Field 3978 (CD received). Optional: legacy snapshots. */
    cdReceivedDate?: string | null;
    piw: boolean;
    llc: { name: string | null; state: string | null; orgType: string | null; ein: string | null };
    settlement: { agent: string | null; contact: string | null; phone: string | null; email: string | null };
    insurance: Record<string, unknown>;
  };
  team: DocOrderTeamMember[];
  teamUnresolved: boolean;
}

export interface DocOrderSettlement {
  agent: string | null;
  contact: string | null;
  phone: string | null;
  email: string | null;
  ccRecipients?: string[];
}

/** One requested fee change in an LO change-request (server-normalized). */
export interface LoChangeItem {
  id: string;
  name: string;
  fromAmount: number;
  toAmount: number;
  amtChanged: boolean;
  fromReimburse: boolean;
  toReimburse: boolean;
  reimbChanged: boolean;
}

export interface LoChangeRequest {
  note: string;
  by: string;
  at: string;
  items: LoChangeItem[];
}

/** Over-capacity escalation stamped by escalate-lender-credit (E3). */
export interface LenderCreditEscalation {
  amount: number;
  capacity: number;
  by: string;
  at: string;
  notify?: { to: string; cc: string[] };
}

/** 4794 grouped-save tracking (ack-pending state). */
export type LenderCreditWriteStatus =
  | 'pending'
  | 'waiting_unlock'
  | 'waiting_retry'
  | 'validated'
  | 'failed';

/** Server-computed lender-credit capacity for the LO view (P&P cap model). */
export interface DocOrderCapacity {
  loanPrice?: number;
  maxAllowed4794: number;
  headroom: number;
  current4794: number;
  floor: number;
  floorSource?: 'latest_le' | 'none';
  floorDisclosedAt?: string | null;
  basis: 'lock' | 'live' | 'none';
}

/** LO comp estimate (recon-initialization parity). */
export interface DocOrderLoProfile {
  comp: number;
  bps: number | null;
  compType: string | null;
}

export interface DocOrderLoSummaryPayload {
  orderId: string;
  revision: number;
  capacity: DocOrderCapacity;
  loProfile: DocOrderLoProfile | null;
}

/** Email claim row surfaced for the Resend affordance. */
export interface DocOrderEmailClaim {
  id: string;
  emailType: 'e1' | 'e2' | 'e3' | 'e4' | 'e5';
  dedupeKey: string;
  status: 'claimed' | 'sent' | 'failed';
  sentAt: string | null;
  lastError: string | null;
}

/** Grid write-back gate + convergence state (never claim synced until it is). */
export interface DocOrderWritebacks {
  enabled: boolean;
  reason: string;
  pendingFields: number;
  lenderCreditWriteStatus: LenderCreditWriteStatus | null;
}

/** send-to-lo / retry-writes report the outbox drain outcome. */
export interface DocOrderDrain {
  pushed: number;
  failed: number;
}

/** doc-order-upload target — an invoice on a fee, or a §6 checklist doc. */
export type DocOrderUploadTarget =
  | { kind: 'invoice'; feeId: string; folder: string }
  | { kind: 'doc'; checklistId: string };

/** doc-order-upload verdict — the AI audit outcome for one uploaded file. */
export interface DocOrderUploadResult {
  verdict: 'accepted' | 'rejected';
  reason: string;
  /** Present on a low-confidence accept (accepted-and-flagged). */
  flag?: string | null;
  /** What the classifier read the document as (rejects carry it). */
  detected?: string | null;
  /** The per-upload id (= FeeInvoice.uid) once accepted + enqueued. */
  uploadId?: string;
}

/** Snake_case order header row as it arrives from the edge function. */
export interface DocOrderRow {
  id: string;
  organization_id: string;
  loan_id: string;
  encompass_loan_id: string;
  sequence: number;
  revision: number;
  stage: DocOrderStage;
  encompass_snapshot: DocOrderSnapshot | null;
  snapshot_pulled_at: string | null;
  snapshot_refreshed_at: string | null;
  vesting: string | null;
  encompass_vesting: string | null;
  vesting_confirmed: boolean;
  vesting_confirmed_at: string | null;
  vesting_workflow: Partial<VestingWorkflowState> | null;
  /** Closing attestation that the live Encompass vesting is accurate (§A1.5). */
  encompass_vesting_confirmed?: boolean;
  encompass_vesting_confirmed_at?: string | null;
  encompass_vesting_confirmed_by?: string | null;
  encompass_vesting_pushed_at?: string | null;
  settlement: DocOrderSettlement | null;
  insurance: InsuranceInfo | null;
  checklist: Record<string, Record<string, unknown>> | null;
  signing_date: string | null;
  closing_date: string | null;
  first_payment_date: string | null;
  /** R5 A2 — the closer's tracked override; display/PDF use month ?? derived. */
  first_payment_override: {
    month: string;
    reason: string;
    actor_user_id: string | null;
    actor_name: string | null;
    at: string;
    derived_month: string | null;
  } | null;
  power_of_attorney: string | null;
  sign_type: string | null;
  doc_email: string | null;
  doc_dating: 'fund-into' | 'next-month' | null;
  lender_credit: number | null;
  lender_credit_disclosed: number | null;
  lender_credit_escalation?: LenderCreditEscalation | null;
  lender_credit_write_group_id?: string | null;
  lender_credit_write_status?: LenderCreditWriteStatus | null;
  lo_change_request?: LoChangeRequest | null;
  acknowledged_at?: string | null;
  acknowledged_by?: string | null;
  signed_at: string | null;
  signed_by: string | null;
  submitted_at?: string | null;
  sent_to_lo_at?: string | null;
  returned_at?: string | null;
  completed_at?: string | null;
  updated_at?: string | null;
}

/** Active eFolder files behind a fee's invoice dot — feeds the eye popover.
 *  storagePath is the loan-documents path when the bytes were downloaded (#7 —
 *  the popover item is clickable → signed URL → new tab); null for a
 *  metadata-only Encompass mirror row (item shown, but not openable).
 *  attachmentId is the Encompass attachment identity — the ONLY key mirror
 *  docs may be deduped by against filed upload entries (never filename). */
export interface DocOrderFolderDocs {
  folder: string;
  docs: Array<{ file: string; at: string | null; storagePath?: string | null; attachmentId?: string | null }>;
}

export interface DocOrderAggregate {
  order: DocOrderRow;
  fees: DocOrderFee[];
  derivedDocs: ChecklistDoc[];
  invoiceFolderStatus: Record<string, boolean>;
  invoiceFolderDocs: Record<string, DocOrderFolderDocs>;
  viewer: DocOrderViewer;
  snapshotStale: boolean;
  /** LO-view lender-credit capacity (present at lo_review/complete for LO viewers). */
  capacity: DocOrderCapacity | null;
  loProfile: DocOrderLoProfile | null;
  emailClaims: DocOrderEmailClaim[];
  writebacks: DocOrderWritebacks;
}

const EMPTY_WRITEBACKS: DocOrderWritebacks = {
  enabled: false,
  reason: '',
  pendingFields: 0,
  lenderCreditWriteStatus: null
};

/** commit-marks (R4 A7): typed per-attachment outcome — the sheet renders
 *  per-file results and offers a retry when any flip failed. */
export interface DocOrderMarkResult {
  attachmentId: string;
  file: string | null;
  markedCurrent: boolean;
  ok: boolean;
  error: string | null;
}

export interface DocOrderMarksOutcome {
  flipped: number;
  skipped: number;
  errors: string[];
  results: DocOrderMarkResult[];
}

/** Normalized outcome for every action. `aggregate` is set whenever the response
 *  carries a full order (create/refresh/save/sign, and open when one exists). */
export interface DocOrderResponse {
  ok: boolean;
  status: number;
  aggregate: DocOrderAggregate | null;
  viewer: DocOrderViewer | null;
  error?: string;
  code?: string;
  blockers?: string[];
  retryable?: boolean;
  aggregateUnavailable?: boolean;
  channel?: string;
  /** send-to-lo / retry-writes: grid-outbox drain outcome. */
  drain?: DocOrderDrain;
  /** lo-acknowledge: unconverged 4794 write — stage STAYS lo_review. */
  pendingWrite?: { groupId: string | null; status: string };
  /** download-pdf. */
  pdfBase64?: string;
  filename?: string;
  /** doc-order-upload: the AI audit verdict for the uploaded file. */
  upload?: DocOrderUploadResult;
  /** commit-marks: per-attachment eFolder flip outcomes (R4 A7). */
  marks?: DocOrderMarksOutcome;
  /** escalate-fee: the SERVER-derived escalation kind (toast copy branches). */
  escalation?: { kind: 'missing_invoice' | 'cost_to_cure' };
  loSummary?: DocOrderLoSummaryPayload;
}

type ResponseBody = Partial<DocOrderAggregate> & {
  order?: DocOrderRow | null;
  viewer?: DocOrderViewer | null;
  error?: string;
  code?: string;
  blockers?: string[];
  retryable?: boolean;
  aggregateUnavailable?: boolean;
  channel?: string;
  drain?: DocOrderDrain;
  pendingWrite?: { groupId: string | null; status: string };
  pdfBase64?: string;
  filename?: string;
  upload?: DocOrderUploadResult;
  marks?: DocOrderMarksOutcome;
  escalation?: { kind: 'missing_invoice' | 'cost_to_cure' };
  loSummary?: DocOrderLoSummaryPayload;
};

interface FunctionsHttpErrorLike {
  message: string;
  context?: { status?: number; json?: () => Promise<unknown> };
}

// Normalize supabase-js's { data, error } into a status + parsed body. A non-2xx
// response surfaces the JSON body on error.context (async), so the hook can still
// branch on 409/422/502 codes; a transport failure collapses to status 0.
async function normalizeInvokeResult(data: unknown, error: unknown): Promise<{ status: number; body: ResponseBody }> {
  if (error) {
    const ctx = (error as FunctionsHttpErrorLike).context;
    if (ctx && typeof ctx.json === 'function') {
      let parsed: ResponseBody = {};
      try {
        parsed = ((await ctx.json()) as ResponseBody) ?? {};
      } catch {
        parsed = { error: (error as FunctionsHttpErrorLike).message };
      }
      return { status: ctx.status ?? 500, body: parsed };
    }
    return { status: 0, body: { error: (error as FunctionsHttpErrorLike).message } };
  }
  return { status: 200, body: (data as ResponseBody) ?? {} };
}

async function invokeDocOrder(body: Record<string, unknown>): Promise<{ status: number; body: ResponseBody }> {
  // Round-5 #4 (mixed-version safety): the edge returns the degraded
  // { aggregateUnavailable } success shape ONLY to bundles that declare they
  // can handle it — an OLD bundle keeps the strict retryable-error behavior
  // it already understands, so the edge can deploy first.
  const { data, error } = await supabase.functions.invoke('doc-order', {
    body: { ...body, supportsAggregateUnavailable: true, supportsLazyLoSummary: true }
  });
  return normalizeInvokeResult(data, error);
}

// Multipart sibling for the doc-order-upload function (FormData body). supabase-js
// sets the multipart boundary itself when the body is a FormData instance.
async function invokeDocOrderUpload(form: FormData): Promise<{ status: number; body: ResponseBody }> {
  const { data, error } = await supabase.functions.invoke('doc-order-upload', { body: form });
  return normalizeInvokeResult(data, error);
}

function toResponse(status: number, body: ResponseBody): DocOrderResponse {
  const hasOrder = !!body.order && typeof body.order === 'object';
  const aggregate: DocOrderAggregate | null = hasOrder
    ? {
        order: body.order as DocOrderRow,
        fees: body.fees ?? [],
        derivedDocs: body.derivedDocs ?? [],
        invoiceFolderStatus: body.invoiceFolderStatus ?? {},
        invoiceFolderDocs: body.invoiceFolderDocs ?? {},
        viewer: (body.viewer as DocOrderViewer) ?? {
          canEditDraft: false,
          canReviewSubmitted: false,
          canActAsLo: false,
          isElevated: false
        },
        snapshotStale: !!body.snapshotStale,
        capacity: body.capacity ?? null,
        loProfile: body.loProfile ?? null,
        emailClaims: body.emailClaims ?? [],
        writebacks: body.writebacks ?? EMPTY_WRITEBACKS
      }
    : null;
  return {
    ok: status >= 200 && status < 300,
    status,
    aggregate,
    viewer: (body.viewer as DocOrderViewer) ?? aggregate?.viewer ?? null,
    error: body.error,
    code: body.code,
    blockers: body.blockers,
    retryable: body.retryable,
    aggregateUnavailable: body.aggregateUnavailable,
    channel: body.channel,
    drain: body.drain,
    pendingWrite: body.pendingWrite,
    pdfBase64: body.pdfBase64,
    filename: body.filename,
    upload: body.upload,
    marks: body.marks,
    escalation: body.escalation,
    loSummary: body.loSummary
  };
}

export type SaveDraftSection = 'closing' | 'settlement' | 'insurance' | 'vesting' | 'checklist';

export const docOrderService = {
  async open(loanId: string): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'open', loanId });
    return toResponse(status, body);
  },

  async create(loanId: string): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'create', loanId });
    return toResponse(status, body);
  },

  async refresh(orderId: string, expectedRevision: number): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'refresh', orderId, expectedRevision });
    return toResponse(status, body);
  },

  /** Lightweight upload-status convergence (Batch 3.1 F4) — NOT `refresh`:
   *  no Encompass fact pull, no revision bump (guarded system convergence),
   *  so deliberately NO expectedRevision in the contract. */
  async checkUploads(orderId: string): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'check-uploads', orderId });
    return toResponse(status, body);
  },

  async loadLoSummary(orderId: string, expectedRevision: number): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'load-lo-summary', orderId, expectedRevision });
    return toResponse(status, body);
  },

  async saveSection(
    orderId: string,
    expectedRevision: number,
    section: SaveDraftSection,
    patch: Record<string, unknown>
  ): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'save-draft', orderId, expectedRevision, section, patch });
    return toResponse(status, body);
  },

  async saveFee(
    orderId: string,
    expectedRevision: number,
    feeId: string,
    feePatch: Partial<DocOrderFee>
  ): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({
      action: 'save-draft',
      orderId,
      expectedRevision,
      section: 'fees',
      feeId,
      feePatch
    });
    return toResponse(status, body);
  },

  async addManualFee(
    orderId: string,
    expectedRevision: number,
    feePatch: { name: string; payee?: string | null; amount?: number }
  ): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({
      action: 'save-draft',
      orderId,
      expectedRevision,
      section: 'fees',
      addManualFee: true,
      feePatch
    });
    return toResponse(status, body);
  },

  async sign(orderId: string, expectedRevision: number): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'sign', orderId, expectedRevision });
    return toResponse(status, body);
  },

  // ---- P2+P3 workflow transitions -------------------------------------------

  async deferInsurance(orderId: string, expectedRevision: number, reason: string): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'defer-insurance', orderId, expectedRevision, reason });
    return toResponse(status, body);
  },

  async submit(orderId: string, expectedRevision: number): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'submit', orderId, expectedRevision });
    return toResponse(status, body);
  },

  async returnToProcessor(orderId: string, expectedRevision: number): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'return-to-processor', orderId, expectedRevision });
    return toResponse(status, body);
  },

  /** R7 — reopen a SIGNED (complete) order for changes (closer/elevated, reason required). */
  async reopenDocOrder(orderId: string, expectedRevision: number, reason: string): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'reopen-doc-order', orderId, expectedRevision, reason });
    return toResponse(status, body);
  },

  async closingEditFee(
    orderId: string,
    expectedRevision: number,
    feeId: string,
    feePatch: Partial<DocOrderFee>
  ): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'closing-edit-fee', orderId, expectedRevision, feeId, feePatch });
    return toResponse(status, body);
  },

  async approveFee(orderId: string, expectedRevision: number, feeId: string, approve: boolean): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({
      action: approve ? 'approve-fee' : 'unapprove-fee',
      orderId,
      expectedRevision,
      feeId
    });
    return toResponse(status, body);
  },

  async vestingAttest(orderId: string, expectedRevision: number): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'vesting-attest', orderId, expectedRevision });
    return toResponse(status, body);
  },

  async resolveChangeRequest(orderId: string, expectedRevision: number): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'resolve-change-request', orderId, expectedRevision });
    return toResponse(status, body);
  },

  async sendToLo(orderId: string, expectedRevision: number): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'send-to-lo', orderId, expectedRevision });
    return toResponse(status, body);
  },

  async setLenderCredit(orderId: string, expectedRevision: number, amount: number): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'set-lender-credit', orderId, expectedRevision, amount });
    return toResponse(status, body);
  },

  async escalateLenderCredit(orderId: string, expectedRevision: number, amount: number): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'escalate-lender-credit', orderId, expectedRevision, amount });
    return toResponse(status, body);
  },

  async withdrawLenderCreditEscalation(orderId: string, expectedRevision: number): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({
      action: 'withdraw-lender-credit-escalation',
      orderId,
      expectedRevision
    });
    return toResponse(status, body);
  },

  async loRequestChanges(
    orderId: string,
    expectedRevision: number,
    note: string,
    items: Array<{ id: string; toAmount: number; toReimburse: boolean }>
  ): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'lo-request-changes', orderId, expectedRevision, note, items });
    return toResponse(status, body);
  },

  async loAcknowledge(orderId: string, expectedRevision: number): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'lo-acknowledge', orderId, expectedRevision });
    return toResponse(status, body);
  },

  async retryWrites(orderId: string, expectedRevision: number): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'retry-writes', orderId, expectedRevision });
    return toResponse(status, body);
  },

  // ---- P4 uploads / mark-current / reset -----------------------------------

  /** Multipart upload → server-side Docling extraction + AI audit → eFolder queue.
   *  The response carries `upload.verdict` alongside the refreshed aggregate. */
  async uploadDocOrderFile(params: {
    orderId: string;
    expectedRevision: number;
    file: File;
    fileName: string;
    target: DocOrderUploadTarget;
  }): Promise<DocOrderResponse> {
    const form = new FormData();
    form.append('file', params.file, params.fileName);
    form.append('orderId', params.orderId);
    form.append('expectedRevision', String(params.expectedRevision));
    form.append('targetKind', params.target.kind);
    form.append('fileName', params.fileName);
    if (params.target.kind === 'invoice') {
      form.append('feeId', params.target.feeId);
      form.append('folder', params.target.folder);
    } else {
      form.append('checklistId', params.target.checklistId);
    }
    const { status, body } = await invokeDocOrderUpload(form);
    return toResponse(status, body);
  },

  /** Commit the fee's live Mark-Current preview to the eFolder (updateAttachment). */
  async commitMarks(orderId: string, expectedRevision: number, feeId: string): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'commit-marks', orderId, expectedRevision, feeId });
    return toResponse(status, body);
  },

  async escalateFee(orderId: string, expectedRevision: number, feeId: string, note: string): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'escalate-fee', orderId, expectedRevision, feeId, note });
    return toResponse(status, body);
  },

  async undoEscalation(orderId: string, expectedRevision: number, feeId: string): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'undo-escalation', orderId, expectedRevision, feeId });
    return toResponse(status, body);
  },

  async setFirstPaymentOverride(
    orderId: string,
    expectedRevision: number,
    input: { month: string; reason: string } | { clear: true }
  ): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'set-first-payment-override', orderId, expectedRevision, ...input });
    return toResponse(status, body);
  },

  /** Reopen a cleared §6 checklist doc for a replacement upload (reset-document). */
  async resetDocument(orderId: string, expectedRevision: number, checklistId: string): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'reset-document', orderId, expectedRevision, checklistId });
    return toResponse(status, body);
  },

  /** Remove a user-added / auto-injected fee (manual or auto_trust only, #20). */
  async deleteFee(orderId: string, expectedRevision: number, feeId: string): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'delete-fee', orderId, expectedRevision, feeId });
    return toResponse(status, body);
  },

  async resendEmail(orderId: string, expectedRevision: number, claimId: string): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'resend-email', orderId, expectedRevision, claimId });
    return toResponse(status, body);
  },

  async downloadPdf(orderId: string, expectedRevision: number): Promise<DocOrderResponse> {
    const { status, body } = await invokeDocOrder({ action: 'download-pdf', orderId, expectedRevision });
    return toResponse(status, body);
  }
};
